import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth, useTheme, api } from "../App";
import AppShell from "../components/AppShell";
import {
  Check,
  LogOut,
  Brain,
  Moon,
  Sun,
  Cloud,
  GitBranch,
  Shield,
  Code,
  Smartphone,
  BarChart,
  Link,
  Settings,
  Layout,
  Palette,
  Rocket,
  Users,
  TrendingUp,
  Github,
  Terminal,
  Save,
  Linkedin,
  ExternalLink,
  List,
  Flame,
  BookOpen,
  Lightbulb,
  FlaskConical,
  Upload,
  FileText,
} from "lucide-react";

const SENIORITY_OPTIONS = [
  { value: "software-engineer", label: "Software Engineer" },
  { value: "senior-engineer", label: "Senior Engineer" },
  { value: "staff-engineer", label: "Staff / Principal Engineer" },
  { value: "tech-lead", label: "Tech Lead" },
  { value: "engineering-manager", label: "Engineering Manager" },
  { value: "director", label: "Director" },
  { value: "vp", label: "VP of Engineering" },
  { value: "cto", label: "CTO" },
];

const POST_STYLE_OPTIONS = [
  { value: "technical", label: "Technical Depth", description: "Code examples, architectural breakdowns, framework deep-dives", icon: Code },
  { value: "story", label: "Personal Story", description: "Relatable narratives, lessons learned from real experience", icon: BookOpen },
  { value: "leadership", label: "Leadership Angle", description: "Strategic insights, team dynamics, org-level decisions", icon: TrendingUp },
  { value: "listicle", label: "Listicle / Tips", description: "Numbered takeaways, quick wins, actionable checklists", icon: List },
  { value: "contrarian", label: "Contrarian Take", description: "Hot takes, challenging conventional wisdom, debate-sparking", icon: Flame },
  { value: "case-study", label: "Case Study", description: "Problem → approach → measurable results with real data", icon: FlaskConical },
  { value: "insight", label: "Industry Insight", description: "Trend analysis, market observations, forward-looking takes", icon: Lightbulb },
];

const TECH_KEYWORD_OPTIONS = [
  "Python", "JavaScript", "TypeScript", "Go", "Rust", "Java", "C++", "Swift", "Kotlin", "SQL",
  "React", "Vue", "Angular", "Next.js", "Tailwind CSS",
  "Node.js", "Django", "FastAPI", "Spring Boot", "Rails",
  "AWS", "Azure", "GCP", "Serverless",
  "LLMs", "PyTorch", "TensorFlow", "LangChain", "OpenAI API",
  "Docker", "Kubernetes", "Terraform", "CI/CD", "GitHub Actions",
  "PostgreSQL", "MongoDB", "Redis", "DynamoDB",
  "GraphQL", "REST APIs", "Microservices", "System Design",
];

const TOPIC_ICONS = {
  "ai-ml": Brain,
  "cloud": Cloud,
  "devops": GitBranch,
  "security": Shield,
  "web-dev": Code,
  "mobile": Smartphone,
  "data-science": BarChart,
  "blockchain": Link,
  "engineering": Settings,
  "product": Layout,
  "ux-design": Palette,
  "startup": Rocket,
  "leadership": Users,
  "career": TrendingUp,
  "open-source": Github,
  "programming": Terminal,
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [availableTopics, setAvailableTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [originalTopics, setOriginalTopics] = useState([]);
  const [linkedinProfile, setLinkedinProfile] = useState("");
  const [originalLinkedinProfile, setOriginalLinkedinProfile] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [isPdfUploading, setIsPdfUploading] = useState(false);
  const [pdfUploadResult, setPdfUploadResult] = useState(null); // { name, headline, tech_keywords }

  // Professional profile state
  const [seniority, setSeniority] = useState("");
  const [originalSeniority, setOriginalSeniority] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [originalKeywords, setOriginalKeywords] = useState([]);
  const [preferredStyles, setPreferredStyles] = useState([]);
  const [originalPreferredStyles, setOriginalPreferredStyles] = useState([]);
  const [isSavingProfProfile, setIsSavingProfProfile] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [topicsRes, userTopicsRes, userRes, selfProfileRes] = await Promise.all([
        api.get("/topics"),
        api.get("/user/topics"),
        api.get("/auth/me"),
        api.get("/user/self-profile"),
      ]);

      setAvailableTopics(topicsRes.data.topics);
      setSelectedTopics(userTopicsRes.data.topics);
      setOriginalTopics(userTopicsRes.data.topics);
      setLinkedinProfile(userRes.data.linkedin_profile || "");
      setOriginalLinkedinProfile(userRes.data.linkedin_profile || "");

      const sp = selfProfileRes.data;
      setSeniority(sp.seniority || "");
      setOriginalSeniority(sp.seniority || "");
      setSelectedKeywords(sp.tech_keywords || []);
      setOriginalKeywords(sp.tech_keywords || []);
      setPreferredStyles(sp.preferred_post_style || []);
      setOriginalPreferredStyles(sp.preferred_post_style || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleTopic = (topicId) => {
    setSelectedTopics((prev) => {
      if (prev.includes(topicId)) {
        if (prev.length <= 1) {
          toast.error("You must have at least 1 topic selected");
          return prev;
        }
        return prev.filter((t) => t !== topicId);
      }
      if (prev.length >= 7) {
        toast.error("You can select up to 7 topics");
        return prev;
      }
      return [...prev, topicId];
    });
  };

  const hasChanges = () => {
    if (selectedTopics.length !== originalTopics.length) return true;
    return !selectedTopics.every((t) => originalTopics.includes(t));
  };

  const hasProfileChanges = () => {
    return linkedinProfile !== originalLinkedinProfile;
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      toast.info("No changes to save");
      return;
    }

    setIsSaving(true);
    try {
      await api.post("/user/topics", { topics: selectedTopics });
      setOriginalTopics(selectedTopics);
      toast.success("Topics updated successfully!");
    } catch (error) {
      console.error("Failed to save topics:", error);
      toast.error("Failed to save topics");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!hasProfileChanges()) {
      toast.info("No changes to save");
      return;
    }

    setIsSavingProfile(true);
    try {
      await api.put("/user/profile", { linkedin_profile: linkedinProfile });
      setOriginalLinkedinProfile(linkedinProfile);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Failed to save profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) return;
    setIsPdfUploading(true);
    setPdfUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", pdfFile);
      const res = await api.post("/user/linkedin-pdf", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfUploadResult(res.data);
      setPdfFile(null);
      toast.success("LinkedIn profile imported successfully!");
      // Refresh tech keywords in professional profile section
      const sp = await api.get("/user/self-profile");
      setSelectedKeywords(sp.data.tech_keywords || []);
      setOriginalKeywords(sp.data.tech_keywords || []);
    } catch (error) {
      const detail = error?.response?.data?.detail || "Failed to parse PDF";
      toast.error(detail);
    } finally {
      setIsPdfUploading(false);
    }
  };

  const toggleKeyword = (kw) => {
    setSelectedKeywords((prev) =>
      prev.includes(kw) ? prev.filter((k) => k !== kw) : [...prev, kw]
    );
  };

  const toggleStyle = (value) => {
    setPreferredStyles((prev) => {
      if (prev.includes(value)) return prev.filter((s) => s !== value);
      if (prev.length >= 3) { toast.error("You can select up to 3 styles"); return prev; }
      return [...prev, value];
    });
  };

  const hasProfProfileChanges = () => {
    if (seniority !== originalSeniority) return true;
    if (preferredStyles.length !== originalPreferredStyles.length) return true;
    if (!preferredStyles.every((s) => originalPreferredStyles.includes(s))) return true;
    if (selectedKeywords.length !== originalKeywords.length) return true;
    return !selectedKeywords.every((k) => originalKeywords.includes(k));
  };

  const handleSaveProfProfile = async () => {
    if (!hasProfProfileChanges()) {
      toast.info("No changes to save");
      return;
    }
    setIsSavingProfProfile(true);
    try {
      await api.post("/user/self-profile", {
        seniority: seniority || null,
        tech_keywords: selectedKeywords,
        preferred_post_style: preferredStyles,
      });
      setOriginalSeniority(seniority);
      setOriginalKeywords([...selectedKeywords]);
      setOriginalPreferredStyles([...preferredStyles]);
      toast.success("Professional profile updated!");
    } catch (error) {
      console.error("Failed to save professional profile:", error);
      toast.error("Failed to save professional profile");
    } finally {
      setIsSavingProfProfile(false);
    }
  };

  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
  };

  const getIcon = (iconName) => {
    const IconComponent = TOPIC_ICONS[iconName] || Code;
    return <IconComponent className="w-5 h-5" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  return (
    <AppShell activeNav="settings">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Section */}
        <section className="mb-8 p-6 bg-card rounded-2xl border border-border">
          <h2 className="text-lg font-semibold text-foreground mb-4">Profile</h2>
          <div className="flex items-center gap-4 mb-6">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full"
              />
            ) : (
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-2xl font-semibold">
                {user?.name?.charAt(0) || "U"}
              </div>
            )}
            <div>
              <p className="font-medium text-foreground">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>

          {/* LinkedIn Profile */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <Linkedin className="w-5 h-5 text-[#0A66C2]" />
              <Label htmlFor="linkedin-url" className="text-foreground font-medium">
                LinkedIn Profile
              </Label>
            </div>
            <div className="flex gap-2">
              <Input
                id="linkedin-url"
                type="url"
                placeholder="https://linkedin.com/in/yourprofile"
                value={linkedinProfile}
                onChange={(e) => setLinkedinProfile(e.target.value)}
                data-testid="linkedin-profile-input"
                className="flex-1 bg-background"
              />
              {linkedinProfile && (
                <Button
                  variant="outline"
                  size="icon"
                  asChild
                >
                  <a href={linkedinProfile} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              )}
            </div>
            <Button
              onClick={handleSaveProfile}
              disabled={!hasProfileChanges() || isSavingProfile}
              data-testid="save-profile-btn"
              size="sm"
              className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
            >
              {isSavingProfile ? "Saving..." : "Save Profile"}
            </Button>
          </div>

          {/* LinkedIn PDF Upload */}
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0A66C2]" />
              <Label className="text-foreground font-medium">Import Profile from PDF</Label>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              On LinkedIn, go to your profile → <span className="font-medium text-foreground">More</span> → <span className="font-medium text-foreground">Save to PDF</span>, then upload the file here. We'll extract your experience, skills, and keywords automatically.
            </p>

            <div className="flex gap-2 items-center">
              <label className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background hover:border-primary/50 cursor-pointer transition-colors text-sm text-muted-foreground">
                <Upload className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{pdfFile ? pdfFile.name : "Choose LinkedIn PDF…"}</span>
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => { setPdfFile(e.target.files[0] || null); setPdfUploadResult(null); }}
                />
              </label>
              <Button
                size="sm"
                onClick={handlePdfUpload}
                disabled={!pdfFile || isPdfUploading}
                className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 flex-shrink-0"
              >
                {isPdfUploading ? "Importing…" : "Import"}
              </Button>
            </div>

            {pdfUploadResult && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <Check className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-green-800 dark:text-green-300">
                    {pdfUploadResult.name}
                  </p>
                  {pdfUploadResult.headline && (
                    <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">{pdfUploadResult.headline}</p>
                  )}
                  {pdfUploadResult.tech_keywords?.length > 0 && (
                    <p className="text-green-600 dark:text-green-500 text-xs mt-1">
                      {pdfUploadResult.tech_keywords.length} tech keywords extracted
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Professional Profile Section */}
        <section className="mb-8 p-6 bg-card rounded-2xl border border-border">
          <h2 className="text-lg font-semibold text-foreground mb-6">Professional Profile</h2>

          {/* Seniority */}
          <div className="mb-6">
            <Label className="text-foreground font-medium mb-3 block">Your Role</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SENIORITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSeniority(seniority === opt.value ? "" : opt.value)}
                  className={`p-2.5 rounded-lg border-2 text-left text-sm transition-all duration-150 ${seniority === opt.value ? "border-primary bg-primary/10 text-primary font-medium" : "border-border bg-background hover:border-primary/40 text-foreground"}`}
                >
                  {seniority === opt.value && <Check className="w-3 h-3 inline mr-1 text-primary" />}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Post Style */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-foreground font-medium">Preferred Post Style</Label>
              <span className="text-xs text-muted-foreground">
                <span className={preferredStyles.length === 3 ? "text-primary font-medium" : ""}>{preferredStyles.length}</span> / 3 selected
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {POST_STYLE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isSelected = preferredStyles.includes(opt.value);
                const isDisabled = !isSelected && preferredStyles.length >= 3;
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleStyle(opt.value)}
                    disabled={isDisabled}
                    className={`relative p-3.5 rounded-xl border-2 text-left transition-all duration-150 ${isDisabled ? "opacity-40 cursor-not-allowed" : ""} ${isSelected ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                      <span className={`text-sm font-medium ${isSelected ? "text-primary" : "text-foreground"}`}>{opt.label}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-primary ml-auto flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{opt.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tech Keywords */}
          <div className="mb-6">
            <Label className="text-foreground font-medium mb-1 block">Tech Stack</Label>
            <p className="text-xs text-muted-foreground mb-3">Select technologies you work with — these personalise your post content.</p>
            <div className="flex flex-wrap gap-2">
              {TECH_KEYWORD_OPTIONS.map((kw) => {
                const isSelected = selectedKeywords.includes(kw);
                return (
                  <button
                    key={kw}
                    onClick={() => toggleKeyword(kw)}
                    className={`px-3 py-1 rounded-full text-sm border transition-all duration-150 ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:border-primary/50"}`}
                  >
                    {kw}
                  </button>
                );
              })}
            </div>
            {selectedKeywords.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">{selectedKeywords.length} selected</p>
            )}
          </div>

          <Button
            onClick={handleSaveProfProfile}
            disabled={!hasProfProfileChanges() || isSavingProfProfile}
            size="sm"
            className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {isSavingProfProfile ? "Saving..." : (
              <><Save className="w-3.5 h-3.5 mr-1.5" />Save Professional Profile</>
            )}
          </Button>
        </section>

        {/* Appearance Section */}
        <section className="mb-8 p-6 bg-card rounded-2xl border border-border">
          <h2 className="text-lg font-semibold text-foreground mb-4">Appearance</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === "dark" ? (
                <Moon className="w-5 h-5 text-muted-foreground" />
              ) : (
                <Sun className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <Label className="text-foreground">Dark Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Toggle between light and dark themes
                </p>
              </div>
            </div>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={toggleTheme}
              data-testid="dark-mode-toggle"
            />
          </div>
        </section>

        {/* Topics Section */}
        <section className="mb-8 p-6 bg-card rounded-2xl border border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Topics</h2>
              <p className="text-sm text-muted-foreground">
                Select 1-7 topics for your feed
              </p>
            </div>
            <div className="px-3 py-1 bg-muted rounded-full text-sm">
              <span className="font-medium text-foreground">{selectedTopics.length}</span>
              <span className="text-muted-foreground"> / 7</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {availableTopics.map((topic) => {
              const isSelected = selectedTopics.includes(topic.id);
              return (
                <button
                  key={topic.id}
                  onClick={() => toggleTopic(topic.id)}
                  data-testid={`topic-${topic.id}`}
                  className={`
                    relative p-4 rounded-xl border-2 text-left transition-all duration-200
                    ${isSelected
                      ? "border-primary bg-primary/10"
                      : "border-border bg-background hover:border-primary/40"
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-lg flex items-center justify-center
                      ${isSelected
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                      }
                    `}>
                      {getIcon(topic.id)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{topic.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{topic.description}</p>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <Button
            onClick={handleSave}
            disabled={!hasChanges() || isSaving}
            data-testid="save-topics-btn"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {isSaving ? (
              "Saving..."
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </section>

        {/* Account Section */}
        <section className="p-6 bg-card rounded-2xl border border-border">
          <h2 className="text-lg font-semibold text-foreground mb-4">Account</h2>
          <Button
            variant="outline"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </section>
      </div>
    </AppShell>
  );
};

export default SettingsPage;
